/** Read-only resume bindings. These IDs are not authentication capabilities. */
export interface ResumableOnboardingIntent {
    intentId: string;
    projectId: string;
    projectName: string;
    activationGeneration: number;
    finalizationRequestId: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isGenericOnboardingDestination(destination: string): boolean {
    return destination === '/' || /^\/(?:dashboard|digests)\/?$/.test(destination);
}

export function resumptionEntryDestination(destination: string): string {
    return isGenericOnboardingDestination(destination)
        ? `/resume-project?redirect_to=${encodeURIComponent(destination === '/' ? '/dashboard' : destination)}`
        : destination;
}

export function parseResumableOnboardingIntents(value: unknown): ResumableOnboardingIntent[] {
    if (!value || typeof value !== 'object' || !('intents' in value) || !Array.isArray(value.intents)) {
        throw new Error('Invalid saved-project response');
    }
    const seen = new Set<string>();
    return value.intents.map((row: unknown) => {
        if (!row || typeof row !== 'object') throw new Error('Invalid saved-project binding');
        const item = row as Record<string, unknown>;
        for (const key of ['intent_id', 'project_id', 'finalization_request_id']) {
            if (typeof item[key] !== 'string' || !UUID_PATTERN.test(item[key])) {
                throw new Error('Invalid saved-project identity');
            }
        }
        if (!Number.isSafeInteger(item.activation_generation) || Number(item.activation_generation) < 1
            || typeof item.project_name !== 'string' || !item.project_name.trim()
            || seen.has(item.project_id as string)) {
            throw new Error('Invalid saved-project binding');
        }
        seen.add(item.project_id as string);
        return {
            intentId: item.intent_id as string,
            projectId: item.project_id as string,
            projectName: item.project_name,
            activationGeneration: Number(item.activation_generation),
            finalizationRequestId: item.finalization_request_id as string,
        };
    });
}

export function resumableActivationDestination(intent: ResumableOnboardingIntent): string {
    const query = new URLSearchParams({
        funnel_source: 'direct', funnel_step: 'activation',
        funnel_project_id: intent.projectId, funnel_intent_id: intent.intentId,
        activation_generation: String(intent.activationGeneration),
        finalization_request_id: intent.finalizationRequestId,
    });
    return `/dashboard/${intent.projectId}/activate?${query}`;
}

export function onboardingResumptionEndpoint(isWeb: boolean, hostname?: string): string {
    const path = '/api/onboarding/project-intents/resumable';
    if (!isWeb) return `${(process.env.EXPO_PUBLIC_BASE_URL || 'https://paperzilla.ai').replace(/\/+$/, '')}${path}`;
    return hostname === 'localhost' || hostname === '127.0.0.1' ? `http://localhost:8001${path}` : path;
}
